<script setup lang="ts">
import { onMounted, ref } from 'vue'

const { t } = useI18n()

definePage({
  meta: { action: 'manage', subject: 'all' },
})

const loading = ref(true)
const saving = ref(false)
const notificationMessage = ref('')
const gamesBlocked = ref(false)
const updatedAt = ref('')
const updatedByName = ref('')

let skipToggle = false

const fetchSettings = async () => {
  const data = await $api('/maintenance')

  notificationMessage.value = data.notificationMessage ?? ''
  skipToggle = true
  gamesBlocked.value = data.gamesBlocked ?? false
  await nextTick()
  skipToggle = false
  updatedAt.value = data.updatedAt ?? ''
  updatedByName.value = data.updatedByName ?? ''
}

onMounted(async () => {
  await fetchSettings()
  loading.value = false
})

const saveSettings = async (message: string | null) => {
  saving.value = true
  await $api('/maintenance', {
    method: 'PUT',
    body: {
      notification_message: message,
      games_blocked: gamesBlocked.value,
    },
  })
  await fetchSettings()
  saving.value = false
}

const saveNotification = () => saveSettings(notificationMessage.value || null)

const clearNotification = async () => {
  notificationMessage.value = ''
  await saveSettings(null)
}

const toggleGamesBlocked = () => {
  if (skipToggle)
    return
  saveSettings(notificationMessage.value || null)
}
</script>

<template>
  <div>
    <VCard
      :loading="loading"
      :title="t('maintenance-page.title')"
    >
      <VCardText v-if="!loading">
        <h3 class="text-h6 mb-4">
          {{ t('maintenance-page.notification_section') }}
        </h3>

        <VAlert
          v-if="notificationMessage"
          type="info"
          class="mb-4"
          variant="tonal"
        >
          <span v-dompurify-html="notificationMessage" />
        </VAlert>

        <VTextarea
          v-model="notificationMessage"
          :label="t('maintenance-page.notification_placeholder')"
          :hint="t('maintenance-page.notification_hint')"
          persistent-hint
          rows="3"
          class="mb-4"
        />

        <div class="d-flex gap-2 mb-8">
          <VBtn
            color="primary"
            :loading="saving"
            @click="saveNotification"
          >
            {{ t('maintenance-page.save') }}
          </VBtn>
          <VBtn
            color="secondary"
            variant="outlined"
            :loading="saving"
            :disabled="!notificationMessage"
            @click="clearNotification"
          >
            {{ t('maintenance-page.clear') }}
          </VBtn>
        </div>

        <VDivider class="mb-6" />

        <h3 class="text-h6 mb-4">
          {{ t('maintenance-page.games_section') }}
        </h3>

        <VSwitch
          v-model="gamesBlocked"
          :label="t('maintenance-page.games_blocked_label')"
          color="error"
          :hint="t('maintenance-page.games_blocked_hint')"
          persistent-hint
          class="mb-4"
          @update:model-value="toggleGamesBlocked"
        />

        <VAlert
          v-if="gamesBlocked"
          type="warning"
          variant="tonal"
          class="mb-4"
        >
          {{ t('maintenance-page.games_blocked_message') }}
        </VAlert>

        <VDivider class="my-6" />

        <p
          v-if="updatedAt"
          class="text-body-2 text-medium-emphasis"
        >
          {{ t('maintenance-page.last_updated') }}: {{ new Date(updatedAt).toLocaleString() }}
          <span v-if="updatedByName"> — {{ t('maintenance-page.updated_by') }}: {{ updatedByName }}</span>
        </p>
      </VCardText>
    </VCard>
  </div>
</template>
